package co.maytes.standupbot.dashboard.features.runslist

import co.maytes.standupbot.dashboard.features.standupruns.StandupFilterState
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standuprun.StandupRunFilters
import co.maytes.standupbot.shared.dto.standuprun.StandupRunSummaryDto
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import io.ktor.http.encodeURLQueryComponent
import kotlinx.coroutines.CoroutineScope

class RunsListContainer(
    scope: CoroutineScope,
    private val api: ApiClient,
) : Container<RunsListContract.State, RunsListContract.Intent, RunsListContract.Action> {

    override val store = scope.store<
            RunsListContract.State,
            RunsListContract.Intent,
            RunsListContract.Action,
            >(initialState = RunsListContract.State()) {
        configure(name = "RunsList")
        logging()
        reduce { intent ->
            when (intent) {
                is RunsListContract.Intent.Load -> {
                    updateState { copy(workspaceId = intent.workspaceId) }
                    loadStandups(intent.workspaceId)
                    loadRuns(intent.workspaceId)
                }
                RunsListContract.Intent.LoadMore -> loadMore()
                is RunsListContract.Intent.SetFilters -> {
                    updateState { copy(filters = intent.filters) }
                    currentState.workspaceId?.let { loadRuns(it) }
                }
                RunsListContract.Intent.ClearFilters -> {
                    updateState { copy(filters = StandupFilterState()) }
                    currentState.workspaceId?.let { loadRuns(it) }
                }
                is RunsListContract.Intent.OpenRun ->
                    action(RunsListContract.Action.Navigate("/app/runs/${intent.runId}"))
                RunsListContract.Intent.GoToOnboarding ->
                    action(RunsListContract.Action.Navigate("/app/onboarding/slack"))
            }
        }
    }

    private suspend fun StoreScope<RunsListContract.State, RunsListContract.Action>.loadStandups(
        workspaceId: String,
    ) {
        when (val res = api.get<List<StandupDto>>("/api/v1/workspaces/$workspaceId/standups")) {
            is Result.Ok -> updateState { copy(standups = res.value) }
            is Result.Err -> Unit
        }
    }

    private suspend fun StoreScope<RunsListContract.State, RunsListContract.Action>.loadRuns(
        workspaceId: String,
    ) {
        updateState {
            copy(
                status = if (showsRows) RunsListContract.Status.Refreshing else RunsListContract.Status.Loading,
                hasEntered = showsRows,
            )
        }
        when (val res = fetch(workspaceId, currentState.filters.toFiltersDto(cursor = null))) {
            is Result.Ok ->
                updateState {
                    copy(
                        items = res.value.items,
                        pageInfo = res.value.pageInfo,
                        status = RunsListContract.Status.Loaded,
                    )
                }
            is Result.Err ->
                updateState { copy(status = RunsListContract.Status.Error(res.error)) }
        }
    }

    private suspend fun StoreScope<RunsListContract.State, RunsListContract.Action>.loadMore() {
        val state = currentState
        val workspaceId = state.workspaceId ?: return
        val cursor = state.pageInfo?.nextCursor ?: return
        if (state.status == RunsListContract.Status.LoadingMore) return

        updateState { copy(status = RunsListContract.Status.LoadingMore, hasEntered = true) }
        when (val res = fetch(workspaceId, state.filters.toFiltersDto(cursor = cursor))) {
            is Result.Ok ->
                updateState {
                    copy(
                        items = items + res.value.items,
                        pageInfo = res.value.pageInfo,
                        status = RunsListContract.Status.Loaded,
                    )
                }
            is Result.Err ->
                updateState { copy(status = RunsListContract.Status.Error(res.error)) }
        }
    }

    private suspend fun fetch(
        workspaceId: String,
        filters: StandupRunFilters,
    ): Result<PagedResponse<StandupRunSummaryDto>, ApiError> {
        val query = filters.toQueryParams()
            .joinToString("&") { (k, v) -> "${k.encodeURLQueryComponent()}=${v.encodeURLQueryComponent()}" }
        val path = "/api/v1/workspaces/$workspaceId/runs" + if (query.isNotEmpty()) "?$query" else ""
        return api.get<PagedResponse<StandupRunSummaryDto>>(path)
    }
}

private fun StandupFilterState.toFiltersDto(cursor: String?): StandupRunFilters = StandupRunFilters(
    standupId = standupId,
    status = status,
    scheduledAfter = scheduledAfterIso?.let { runCatching { kotlinx.datetime.Instant.parse(it) }.getOrNull() },
    scheduledBefore = scheduledBeforeIso?.let { runCatching { kotlinx.datetime.Instant.parse(it) }.getOrNull() },
    limit = StandupRunFilters.DEFAULT_LIMIT,
    cursor = cursor,
)
